import { useState } from "react";
import toast from "react-hot-toast";

import { LISBlock, LISCase, LISSlide } from "pwapi";

import { ApiError } from "pwapi/core/ApiError";

import { selectFeatureFlag } from "shared/reducers/sysReducer";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { useBarcodePrintingCallbacks } from "hooks/useBarcodePrintingCallbacks";
import { useSelectedLabStation } from "hooks/useSelectedLabStation";
import Spinner from "ui/components/Spinner";

const PrintSlideButton = ({
  pathCase,
  block,
  stainId,
  isCut = true,
  showButtons = true,
  dispatchDetails,
  dispatchCheck,
}: {
  pathCase: LISCase;
  block: LISBlock;
  stainId?: number | null;
  isCut?: boolean;
  showButtons?: boolean;
  dispatchDetails: {
    func: Function;
    params: { [key: string]: any };
  };
  // dispatchCheck is a boolean to determine whether or not the dispatch function can be called.
  dispatchCheck: boolean;
}) => {
  const dispatch = useAppDispatch();
  const selectedLabStation = useSelectedLabStation();

  const autoPrint = !useAppSelector((state) =>
    selectFeatureFlag(state, "skipAutomaticBarcodePrinting")
  );

  const [slideCount, setSlideCount] = useState<number>(1);
  const [creatingSlides, setCreatingSlides] = useState<boolean>(false);

  const allSlidesPerBlockPerStain = pathCase.slides.filter(
    (s: LISSlide) => s.block_obj_id === block.id && s.stain_id === stainId
  );

  const { printSlide } = useBarcodePrintingCallbacks();
  const onClickPrint = () => {
    if (!dispatchCheck || !block.id) return;

    setCreatingSlides(true);
    dispatch(
      dispatchDetails.func({
        ...dispatchDetails.params,
        parentLookupLabStationId: selectedLabStation.id,
        slideCount,
      })
    )
      .then((response: LISCase) => {
        const newSlideIds = response._meta?.created?.slides || [];
        if (autoPrint) printSlide(newSlideIds, response.id as number);
      })
      .catch((error: ApiError) => {
        toast.error(`Print Slide Error\n${error.body.detail}`);
      })
      .finally(() => {
        setCreatingSlides(false);
      });

    setSlideCount(1);
  };

  return !isCut ? (
    <button className="Button Button-Purple" onClick={onClickPrint}>
      Print Slide
    </button>
  ) : showButtons ? (
    <div className="CuttingStationBlock__Work--horizontalAlign">
      <button
        className="Button Button-Purple"
        disabled={slideCount <= 1 || creatingSlides}
        onClick={() => {
          setSlideCount(slideCount - 1);
        }}
      >
        -
      </button>{" "}
      <button
        className="Button Button-Purple"
        onClick={onClickPrint}
        disabled={creatingSlides}
      >
        {creatingSlides ? (
          <span className="CuttingStationBlock__Work--horizontalAlign">
            <Spinner size="14px" width="2px" /> Creating Slides
          </span>
        ) : (
          <span>
            Print Additional Slide
            {slideCount > 1 && "s"}
            {allSlidesPerBlockPerStain.length > 0 && (
              <>
                {": "}S{allSlidesPerBlockPerStain.length + 1}
                {slideCount > 1
                  ? ` - S${allSlidesPerBlockPerStain.length + slideCount}`
                  : ""}
              </>
            )}
          </span>
        )}
      </button>{" "}
      <button
        className="Button Button-Purple"
        disabled={slideCount >= 9 || creatingSlides}
        onClick={() => {
          setSlideCount(slideCount + 1);
        }}
      >
        +
      </button>
    </div>
  ) : null;
};

export default PrintSlideButton;
